import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { fetchData, postData } from '../../helpers/apiUtils';
import { NfaVnrRecommendation, NfaVnrReservedSend } from '../../helpers/nfaMailingData';

type Phase = 'idle' | 'recommended' | 'reserved' | 'finalized';

const VnrMailingTab: FC = () => {
  const [plannedDate, setPlannedDate] = useState<Dayjs | null>(dayjs().add(1, 'day'));
  const [phase, setPhase] = useState<Phase>('idle');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<NfaVnrRecommendation | null>(null);
  const [reserved, setReserved] = useState<NfaVnrReservedSend | null>(null);
  // SAM IDs the user has skipped via "Anderen SAM laden" — passed to the
  // backend so the next call returns a different SAM. Resets when the date
  // picker changes (different date = different cooldown context).
  const [excludedSamIds, setExcludedSamIds] = useState<number[]>([]);
  const [hydrating, setHydrating] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  const formattedDate = plannedDate ? plannedDate.format('YYYY-MM-DD') : '';

  // Hydrate state on mount + whenever the date picker changes:
  //   - If a prepared/confirmed VNR row exists for this date, jump to the
  //     matching phase so the user keeps seeing their reservation across
  //     page refreshes / tab switches.
  //   - If no active row, reset to idle. Excluded list is reset on every
  //     date change (different date = different cooldown context).
  useEffect(() => {
    if (!formattedDate) return;
    let cancelled = false;
    (async () => {
      setHydrating(true);
      setError(null);
      const result = await fetchData<NfaVnrReservedSend | null>(
        `/api/nfa-mailings/vnr/active?planned_send_date=${formattedDate}`,
      );
      if (cancelled) return;
      setHydrating(false);
      if (result.success && result.data) {
        setReserved(result.data);
        setRecommendation(null);
        setPhase(result.data.status === 'confirmed' ? 'finalized' : 'reserved');
      } else {
        setReserved(null);
        setRecommendation(null);
        setPhase('idle');
      }
      setExcludedSamIds([]);
    })();
    return () => {
      cancelled = true;
    };
  }, [formattedDate]);

  const loadRecommendation = async (excludeOverride?: number[]) => {
    if (!formattedDate) return;
    const exclude = excludeOverride ?? excludedSamIds;
    setLoading(true);
    setError(null);
    setRecommendation(null);
    setReserved(null);
    setPhase('idle');

    const params = new URLSearchParams({ planned_send_date: formattedDate });
    if (exclude.length > 0) {
      params.set('exclude_sam_ids', exclude.join(','));
    }
    const result = await fetchData<NfaVnrRecommendation | null>(
      `/api/nfa-mailings/vnr/recommendation?${params.toString()}`,
    );
    setLoading(false);

    if (!result.success) {
      setError(result.error || 'Fehler beim Laden der Empfehlung');
      return;
    }
    if (!result.data) {
      setError(
        exclude.length > 0
          ? 'Keine weiteren SAMs verfügbar — Cooldown oder Pool erschöpft.'
          : 'Keine SAM-Empfehlung verfügbar (alle SAMs in Cooldown).',
      );
      return;
    }
    setRecommendation(result.data);
    setPhase('recommended');
  };

  // Append the just-shown sam_id to the excluded list and re-query so the next
  // recommendation is guaranteed different. setState is async so we pass the
  // new array directly via excludeOverride to dodge stale-closure reads.
  const loadAnotherRecommendation = async () => {
    const nextExclude = recommendation
      ? [...excludedSamIds, recommendation.sam_id]
      : excludedSamIds;
    setExcludedSamIds(nextExclude);
    await loadRecommendation(nextExclude);
  };

  const reserve = async () => {
    if (!recommendation || !formattedDate) return;
    setLoading(true);
    setError(null);

    const result = await postData<NfaVnrReservedSend>('/api/nfa-mailings/vnr/reserve', {
      sam_id: recommendation.sam_id,
      product_id: recommendation.product_id,
      planned_send_date: formattedDate,
    });
    setLoading(false);

    if (!result.success || !result.data) {
      setError(result.error || 'Fehler beim Reservieren');
      return;
    }
    setReserved(result.data);
    setPhase('reserved');
  };

  const confirm = async () => {
    if (!reserved) return;
    setLoading(true);
    const result = await postData<{ success: boolean }>(
      `/api/nfa-mailings/vnr/sends/${reserved.id}/confirm`,
      {},
    );
    setLoading(false);
    if (!result.success) {
      setSnackbar({
        open: true,
        message: result.error || 'Bestätigung fehlgeschlagen',
        severity: 'error',
      });
      return;
    }
    setSnackbar({ open: true, message: 'Versand bestätigt.', severity: 'success' });
    setPhase('finalized');
  };

  const discard = async () => {
    if (!reserved) return;
    setLoading(true);
    const result = await postData<{ success: boolean }>(
      `/api/nfa-mailings/vnr/sends/${reserved.id}/discard`,
      {},
    );
    setLoading(false);
    if (!result.success) {
      setSnackbar({
        open: true,
        message: result.error || 'Verwerfen fehlgeschlagen',
        severity: 'error',
      });
      return;
    }
    setSnackbar({ open: true, message: 'Reservierung verworfen.', severity: 'info' });
    setReserved(null);
    setRecommendation(null);
    setPhase('idle');
    setExcludedSamIds([]);
  };

  const copyHtml = async () => {
    if (!reserved?.final_html) return;
    try {
      await navigator.clipboard.writeText(reserved.final_html);
      setSnackbar({ open: true, message: 'HTML in Zwischenablage kopiert.', severity: 'success' });
    } catch {
      setSnackbar({
        open: true,
        message: 'Kopieren fehlgeschlagen — HTML manuell aus dem Vorschau-Feld übernehmen.',
        severity: 'error',
      });
    }
  };

  const copyBanner = async () => {
    if (!reserved?.nfa_send_code) return;
    try {
      await navigator.clipboard.writeText(reserved.nfa_send_code);
      setSnackbar({
        open: true,
        message: 'Banner in Zwischenablage kopiert.',
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Kopieren fehlgeschlagen', severity: 'error' });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box mb={2}>
        <Typography variant="body2" color="textSecondary">
          VNR-Kooperationsmailing für die NFA-Liste (one-stock-letter.de). Wählt automatisch den
          punktebesten SAM aus dem External-SAM-Pool. Banner-Format:{' '}
          <code>
            KOOP_&lt;PARTNER&gt;_&lt;SUB&gt;_NFA_YES_&lt;REPORT&gt;_X-LFT-NFA-Send-&lt;ID&gt;-&lt;Datum&gt;
          </code>
        </Typography>
      </Box>

      <Box mb={3} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <DatePicker
          label="Geplantes Sendedatum"
          value={plannedDate}
          onChange={(v) => setPlannedDate(v)}
          format="DD.MM.YYYY"
          slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={() => loadRecommendation()}
          disabled={
            loading || hydrating || !formattedDate || phase === 'reserved' || phase === 'finalized'
          }
        >
          Empfehlung laden
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {(loading || hydrating) && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && recommendation && phase === 'recommended' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" gap={1} mb={1} flexWrap="wrap">
              <Chip label="VNR" size="small" color="warning" />
              <Chip label={recommendation.customer_id || '—'} size="small" variant="outlined" />
              <Chip
                label={`Score: ${recommendation.score.toFixed(2)}`}
                size="small"
                variant="outlined"
              />
            </Stack>
            <Typography variant="h6" gutterBottom>
              {recommendation.subject || '(ohne Betreff)'}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1}>
              <strong>Produkt:</strong> {recommendation.product_name}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1}>
              <strong>Slug:</strong> <code>{recommendation.campaign_slug}</code>
            </Typography>
            {recommendation.preview_text && (
              <Typography variant="body2" color="textSecondary" mb={1}>
                <strong>Preview-Text:</strong> {recommendation.preview_text}
              </Typography>
            )}
            <Typography variant="body2" color="textSecondary" mb={2}>
              <strong>Absender:</strong> {recommendation.from_name}
            </Typography>
            <Box display="flex" gap={2}>
              <Button variant="contained" color="primary" onClick={reserve} disabled={loading}>
                Reservieren & HTML rendern
              </Button>
              <Button
                variant="outlined"
                onClick={loadAnotherRecommendation}
                disabled={loading}
                startIcon={<RefreshIcon />}
              >
                Anderen SAM laden
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {!loading && reserved && (phase === 'reserved' || phase === 'finalized') && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
              <Chip label="VNR" size="small" color="warning" />
              <Chip
                label={phase === 'finalized' ? 'Bestätigt' : 'Reserviert (prepared)'}
                size="small"
                color={phase === 'finalized' ? 'success' : 'warning'}
              />
              <Chip label={`Send ID: ${reserved.id}`} size="small" variant="outlined" />
            </Stack>

            <Typography variant="h6" gutterBottom>
              {reserved.subject}
            </Typography>

            <TextField
              label="Banner / Affiliate"
              value={reserved.nfa_send_code}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                style: { fontFamily: 'monospace', fontSize: '0.85rem' },
              }}
              sx={{ mb: 2 }}
            />

            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <Button
                variant="contained"
                startIcon={<CopyIcon />}
                onClick={copyHtml}
                disabled={loading}
              >
                HTML kopieren
              </Button>
              <Button variant="outlined" startIcon={<CopyIcon />} onClick={copyBanner}>
                Banner kopieren
              </Button>
              {phase === 'reserved' && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={confirm}
                    disabled={loading}
                  >
                    Versand bestätigen
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={discard}
                    disabled={loading}
                  >
                    Verwerfen
                  </Button>
                </>
              )}
            </Box>

            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                overflow: 'hidden',
                mt: 2,
              }}
            >
              <iframe
                title="VNR Send Preview"
                srcDoc={reserved.final_html}
                style={{ width: '100%', height: '600px', border: 'none' }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default VnrMailingTab;
